// app/src/api/core/idempotency.ts
/**
 * POST 재시도 안전화를 위한 멱등키(Idempotency-Key) 처리
 *
 * 중복 POST/PATCH 요청을 방지하여 서버 측에서 안전한 재시도를 가능하게 합니다.
 */

import { v4 as uuidv4 } from "uuid"

/**
 * 멱등키 생성
 *
 * UUID v4 기반으로 고유한 멱등키를 생성합니다.
 *
 * @returns 멱등키 문자열 (예: "idem_550e8400-e29b-41d4-a716-446655440000")
 */
export function generateIdempotencyKey(): string {
  const uuid = uuidv4()
  return `idem_${uuid}`
}

/**
 * 멱등성이 필요한 HTTP 메서드 목록
 */
export const IDEMPOTENT_METHODS = new Set(["POST", "PATCH", "PUT"])

/**
 * 멱등키 헤더명
 */
export const IDEMPOTENCY_HEADER = "Idempotency-Key"

/**
 * 요청에 멱등키가 필요한지 확인
 *
 * @param method HTTP 메서드
 * @returns 멱등키 필요 여부
 */
export function needsIdempotencyKey(method: string): boolean {
  return IDEMPOTENT_METHODS.has(method.toUpperCase())
}

/**
 * 설정에 멱등키 헤더 추가
 *
 * @param config axios 설정 객체
 * @param method HTTP 메서드
 * @returns 멱등키가 추가된 설정 객체
 */
export function addIdempotencyKey(config: any = {}, method: string, idempotencyKey?: string): any {
  if (!needsIdempotencyKey(method)) {
    return config
  }

  // 이미 멱등키가 있으면 추가하지 않음
  if (config.headers?.[IDEMPOTENCY_HEADER]) {
    return config
  }

  // 설정 객체 복사 및 헤더 초기화
  const newConfig = { ...config }
  newConfig.headers = {
    ...config.headers,
    [IDEMPOTENCY_HEADER]: idempotencyKey || generateIdempotencyKey(),
  }

  return newConfig
}

/**
 * 멱등키 캐시 인터페이스
 * 클라이언트 측에서 중복 요청 방지를 위한 간단한 캐시
 */
class IdempotencyCache {
  private cache = new Map<string, { timestamp: number; promise: Promise<any> }>()
  private objectIds = new WeakMap<object, number>()
  private nextObjectId = 1
  private readonly TTL = 5000 // 5초 TTL

  /**
   * 캐시 키 생성
   * 한글 등 멀티바이트 문자를 안전하게 처리
   */
  private generateCacheKey(method: string, url: string, data: any): string {
    const dataStr = this.serializeData(data)

    // UTF-8을 안전하게 Base64로 변환
    const encodedData = this.safeBase64Encode(dataStr)
    return `${method.toUpperCase()}:${url}:${encodedData}`
  }

  private serializeData(data: any): string {
    if (typeof FormData !== "undefined" && data instanceof FormData) {
      const entries: Array<[string, string | { objectId: number; name: string; size: number; type: string; lastModified: number }]> = []
      data.forEach((value, key) => {
        entries.push([
          key,
          typeof value === "string"
            ? value
            : {
                objectId: this.getObjectId(value),
                name: value.name,
                size: value.size,
                type: value.type,
                lastModified: value.lastModified,
              },
        ])
      })
      return JSON.stringify(entries)
    }

    return data ? JSON.stringify(data) : ""
  }

  private getObjectId(value: object): number {
    const existingId = this.objectIds.get(value)
    if (existingId !== undefined) return existingId

    const objectId = this.nextObjectId
    this.nextObjectId += 1
    this.objectIds.set(value, objectId)
    return objectId
  }

  /**
   * 한글 등 멀티바이트 문자를 안전하게 Base64로 인코딩
   */
  private safeBase64Encode(str: string): string {
    try {
      // 브라우저에서 지원하는 경우 TextEncoder 사용
      if (typeof TextEncoder !== "undefined") {
        const encoder = new TextEncoder()
        const bytes = encoder.encode(str)

        // Uint8Array를 바이너리 문자열로 변환
        let binaryString = ""
        for (let i = 0; i < bytes.length; i++) {
          binaryString += String.fromCharCode(bytes[i])
        }

        return btoa(binaryString)
      }
    } catch (error) {}

    // fallback: 해시 기반 키 생성
    const hash = this.simpleHash(str)
    return hash.toString(16)
  }

  /**
   * 간단한 해시 함수 (fallback용)
   */
  private simpleHash(str: string): number {
    let hash = 0
    if (str.length === 0) return hash

    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // 32비트 정수로 변환
    }

    return Math.abs(hash)
  }

  /**
   * 진행 중인 동일 요청 확인
   */
  public getPendingRequest(method: string, url: string, data: any): Promise<any> | null {
    const key = this.generateCacheKey(method, url, data)
    const cached = this.cache.get(key)

    if (cached && Date.now() - cached.timestamp < this.TTL) {
      return cached.promise
    }

    return null
  }

  /**
   * 요청을 캐시에 저장하고 wrapped promise 반환
   */
  public setPendingRequest(method: string, url: string, data: any, promise: Promise<any>): Promise<any> {
    const key = this.generateCacheKey(method, url, data)

    // promise를 감싸되, 에러는 그대로 전파
    const wrappedPromise = promise.then(
      result => {
        this.cache.delete(key)
        return result
      },
      error => {
        this.cache.delete(key)
        throw error // 에러를 다시 throw하여 호출자에게 전달
      }
    )

    this.cache.set(key, {
      timestamp: Date.now(),
      promise: wrappedPromise,
    })

    // 주기적 정리
    this.cleanup()

    // wrapped promise를 반환하여 에러 처리가 제대로 전파되도록 함
    return wrappedPromise
  }

  /**
   * 만료된 캐시 정리
   */
  private cleanup(): void {
    if (this.cache.size < 50) return // 50개 미만이면 정리하지 않음

    const now = Date.now()
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp >= this.TTL) {
        this.cache.delete(key)
      }
    }
  }

  /**
   * 전체 캐시 초기화
   */
  public clear(): void {
    this.cache.clear()
  }
}

// 전역 캐시 인스턴스
export const idempotencyCache = new IdempotencyCache()

/**
 * 중복 요청 방지를 위한 래퍼
 *
 * @param method HTTP 메서드
 * @param url 요청 URL
 * @param data 요청 데이터
 * @param requestFn 실제 요청 함수
 * @returns Promise (에러 처리 포함)
 */
export async function preventDuplicateRequest<T>(method: string, url: string, data: any, requestFn: () => Promise<T>): Promise<T> {
  // POST/PATCH/PUT만 중복 방지 적용
  if (!needsIdempotencyKey(method)) {
    return requestFn()
  }

  // 진행 중인 동일 요청 확인
  const pendingRequest = idempotencyCache.getPendingRequest(method, url, data)
  if (pendingRequest) {
    return pendingRequest as Promise<T>
  }

  // 새 요청 시작하고 wrapped promise 받기 (에러 처리 포함)
  const wrappedPromise = idempotencyCache.setPendingRequest(method, url, data, requestFn())

  // 캐시된 wrapped promise를 반환하여 에러가 제대로 전파되도록 함
  return wrappedPromise as Promise<T>
}
